import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { AuthPage } from "@/features/auth/AuthPage";
import { McpOAuthConsentPage } from "@/features/auth/McpOAuthConsentPage";
import { CliDeviceConsentPage } from "@/features/auth/CliDeviceConsentPage";
import { ResetPasswordPage } from "@/features/auth/ResetPasswordPage";
import { VerifyEmailPage } from "@/features/auth/VerifyEmailPage";
import { DevMailboxPage } from "@/features/dev/DevMailboxPage";
import { LauncherPage } from "@/features/launcher/LauncherPage";
import { PricingPage } from "@/features/pricing/PricingPage";
import { OrganizationBuildPage, type ExtensionsTab } from "@/features/build/OrganizationBuildPage";
import { CreateOrganizationPage } from "@/features/organizations/CreateOrganizationPage";
import { OrganizationDataPage } from "@/features/organizations/OrganizationDataPage";
import { OrganizationEntitiesPage } from "@/features/organizations/OrganizationEntitiesPage";
import { OrganizationExtensionSettingsPage } from "@/features/organizations/OrganizationExtensionSettingsPage";
import { OrganizationSecretsPage } from "@/features/organizations/OrganizationSecretsPage";
import type { OrganizationView } from "@/features/organizations/OrganizationNavigation";
import { saveOrganizationId } from "@/features/organizations/selected-organization";
import { MarketingPage } from "@/features/marketing/MarketingPage";
import {
  ProductPage,
  ProductsIndexPage,
} from "@/features/products/ProductsPages";
import {
  type PublicInformationKind,
  PublicInformationPage,
} from "@/features/public-information/PublicInformationPage";
import {
  RequireAnonymous,
  RequireAuth,
  RequireBusiness,
} from "@/lib/route-guards";
import { useSession } from "@/lib/session";

const rootRoute = createRootRoute({ component: Outlet });

const marketingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => (
    <RequireAnonymous>
      <MarketingPage />
    </RequireAnonymous>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: () => (
    <RequireAnonymous>
      <AuthPage />
    </RequireAnonymous>
  ),
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: () => (
    <RequireAnonymous>
      <AuthPage initialMode="signup" />
    </RequireAnonymous>
  ),
});

function McpOAuthConsentRoute() {
  const { interaction } = mcpOAuthConsentRoute.useSearch();
  const { state } = useSession();

  useEffect(() => {
    if (state.status === "anonymous" && interaction) {
      window.location.replace(`/login?oauth_interaction=${encodeURIComponent(interaction)}`);
    }
  }, [interaction, state.status]);

  if (!interaction) {
    return <main className="mx-auto max-w-xl px-6 py-16"><p role="alert">This authorization request could not be verified.</p></main>;
  }
  if (state.status === "loading" || state.status === "anonymous") {
    return <div className="grid min-h-screen place-items-center text-sm text-[var(--muted-foreground)]">Loading...</div>;
  }
  return <McpOAuthConsentPage interactionId={interaction} />;
}

const mcpOAuthConsentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mcp/consent",
  validateSearch: (search: Record<string, unknown>) => ({
    interaction:
      typeof search.interaction === "string" && /^[A-Za-z0-9_-]{43}$/.test(search.interaction)
        ? search.interaction
        : undefined,
  }),
  component: McpOAuthConsentRoute,
});

const cliDeviceRoute = createRoute({ getParentRoute: () => rootRoute, path: "/oauth/device", component: CliDeviceConsentPage });

// Public on purpose: the link is opened from a mail client, possibly while
// signed in as someone else.
// Product pages are public for everyone (crawlers, signed-out and signed-in
// visitors alike), unlike "/", which sends signed-in people to the launcher.
const productsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/products",
  component: ProductsIndexPage,
});

const productRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/products/$slug",
  component: ProductPage,
});

const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pricing",
  component: PricingPage,
});

function DocsRedirect() {
  useEffect(() => {
    const current = new URL(window.location.href);
    window.location.replace(
      `https://docs.sylviq.io${current.pathname}${current.search}${current.hash}`,
    );
  }, []);
  return (
    <main className="mx-auto max-w-xl px-6 py-16">
      <p role="status" className="text-sm text-[var(--muted-foreground)]">
        Opening documentation…
      </p>
    </main>
  );
}

const docsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/docs",
  component: DocsRedirect,
});

const docsSplatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/docs/$",
  component: DocsRedirect,
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify-email",
  component: VerifyEmailPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
});

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/home",
  component: () => (
    <RequireAuth>
      <LauncherPage />
    </RequireAuth>
  ),
});

const organizationCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/organizations/new",
  component: function CreateOrganizationRoute() {
    const navigate = organizationCreateRoute.useNavigate();
    return (
      <RequireAuth>
        <CreateOrganizationPage
          onCreated={(organization) => {
            saveOrganizationId(organization.id);
            void navigate({
              to: "/organizations/$organizationId/entities",
              params: { organizationId: organization.id },
            });
          }}
        />
      </RequireAuth>
    );
  },
});

function organizationToolRoute(view: Exclude<OrganizationView, "extensions" | "entities" | "data" | "secrets">) {
  const route = createRoute({
    getParentRoute: () => rootRoute,
    path: `/organizations/$organizationId/${view}`,
    component: () => {
      const { organizationId } = route.useParams();
      return <RequireAuth><RequireBusiness organizationId={organizationId}><OrganizationBuildPage key={`${organizationId}:${view}`} organizationId={organizationId} view={view} /></RequireBusiness></RequireAuth>;
    },
  });
  return route;
}
const organizationAccessRoute = organizationToolRoute("access");

const organizationEntitiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/organizations/$organizationId/entities",
  component: () => {
    const { organizationId } = organizationEntitiesRoute.useParams();
    return (
      <RequireAuth>
        <RequireBusiness organizationId={organizationId}>
          <OrganizationEntitiesPage organizationId={organizationId} />
        </RequireBusiness>
      </RequireAuth>
    );
  },
});

const organizationDataRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/organizations/$organizationId/data",
  component: () => {
    const { organizationId } = organizationDataRoute.useParams();
    return (
      <RequireAuth>
        <RequireBusiness organizationId={organizationId}>
          <OrganizationDataPage organizationId={organizationId} />
        </RequireBusiness>
      </RequireAuth>
    );
  },
});

const organizationExtensionSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/organizations/$organizationId/settings/$contributionId",
  component: () => {
    const { organizationId, contributionId } = organizationExtensionSettingsRoute.useParams();
    return (
      <RequireAuth>
        <RequireBusiness organizationId={organizationId}>
          <OrganizationExtensionSettingsPage
            organizationId={organizationId}
            contributionId={contributionId}
          />
        </RequireBusiness>
      </RequireAuth>
    );
  },
});

const organizationSecretsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/organizations/$organizationId/secrets",
  component: () => {
    const { organizationId } = organizationSecretsRoute.useParams();
    return (
      <RequireAuth>
        <OrganizationSecretsPage organizationId={organizationId} />
      </RequireAuth>
    );
  },
});

type OrganizationExtensionsSearch = {
  tab: "installed" | "updates" | "curated" | undefined;
};

export function validateOrganizationExtensionsSearch(
  search: Record<string, unknown>,
): OrganizationExtensionsSearch {
  return {
    tab:
      search.tab === "installed" || search.tab === "updates" || search.tab === "curated"
        ? search.tab
        : undefined,
  };
}

const organizationExtensionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/organizations/$organizationId/extensions",
  validateSearch: (search: Record<string, unknown>) =>
    validateOrganizationExtensionsSearch(search),
  component() {
    const { organizationId } = organizationExtensionsRoute.useParams();
    const { tab } = organizationExtensionsRoute.useSearch() as OrganizationExtensionsSearch;
    const navigate = organizationExtensionsRoute.useNavigate();
    return (
      <RequireAuth>
        <RequireBusiness organizationId={organizationId}>
          <OrganizationBuildPage
            key={`${organizationId}:extensions`}
            organizationId={organizationId}
            view="extensions"
            extensionsTab={tab ?? "browse"}
            onExtensionsTabChange={(next: ExtensionsTab) => {
              void navigate({
                search: (previous) => ({
                  ...(previous as OrganizationExtensionsSearch),
                  tab: next === "browse" ? undefined : next,
                }),
              });
            }}
          />
        </RequireBusiness>
      </RequireAuth>
    );
  },
});

function informationPage(kind: PublicInformationKind) {
  return () => <PublicInformationPage kind={kind} />;
}

const supportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/support",
  component: informationPage("support"),
});
const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/legal/privacy",
  component: informationPage("privacy"),
});
const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/legal/terms",
  component: informationPage("terms"),
});
const cookiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/legal/cookies",
  component: informationPage("cookies"),
});
const acceptableUseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/legal/acceptable-use",
  component: informationPage("acceptable-use"),
});
const subprocessorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/legal/subprocessors",
  component: informationPage("subprocessors"),
});
const securityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/security",
  component: informationPage("security"),
});

const localMailboxRoute =
  import.meta.env.VITE_NICHE_TEST_MODE === "fullstack"
    ? createRoute({
        getParentRoute: () => rootRoute,
        path: "/__dev/mailbox",
        component: DevMailboxPage,
      })
    : null;

const routeTree = rootRoute.addChildren([
  marketingRoute,
  loginRoute,
  signupRoute,
  mcpOAuthConsentRoute,
  cliDeviceRoute,
  productsRoute,
  productRoute,
  pricingRoute,
  docsRoute,
  docsSplatRoute,
  verifyEmailRoute,
  resetPasswordRoute,
  homeRoute,
  organizationCreateRoute,
  organizationEntitiesRoute,
  organizationDataRoute,
  organizationExtensionSettingsRoute,
  organizationSecretsRoute,
  organizationExtensionsRoute,
  organizationAccessRoute,
  supportRoute,
  privacyRoute,
  termsRoute,
  cookiesRoute,
  acceptableUseRoute,
  subprocessorsRoute,
  securityRoute,
  ...(localMailboxRoute ? [localMailboxRoute] : []),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
